import { useEffectPlease } from "@features/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { ScrollArea } from "@radix-ui/themes";
import { motion } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

interface EnhancedScrollAreaProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  hideControls?: boolean;
}

export const EnhancedScrollArea = ({
  children,
  className,
  contentClassName,
  hideControls = false,
}: EnhancedScrollAreaProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isLeftButtonPressed, setIsLeftButtonPressed] = useState(false);
  const [isRightButtonPressed, setIsRightButtonPressed] = useState(false);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Drag scrolling state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);

  // Check if it's a touch device
  useEffectPlease(() => {
    const touchQuery = window.matchMedia("(pointer: coarse)");
    setIsTouchDevice(touchQuery.matches);

    const updateTouchStatus = (e: MediaQueryListEvent) => {
      setIsTouchDevice(e.matches);
    };

    touchQuery.addEventListener("change", updateTouchStatus);
    return () => touchQuery.removeEventListener("change", updateTouchStatus);
  }, []);

  // Check for overflow and update arrow visibility
  const checkForOverflow = () => {
    if (scrollContainerRef.current && contentRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current?.querySelector(
          "[data-radix-scroll-area-viewport] .overflow-auto"
        ) as HTMLElement;

      const hasOverflow = scrollWidth > clientWidth;

      if (!hasOverflow) {
        setShowLeftArrow(false);
        setShowRightArrow(false);
        return;
      }

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding errors
    }
  };

  useEffectPlease(() => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport] .overflow-auto"
    ) as HTMLElement;

    if (scrollContainer) {
      // Initial check
      checkForOverflow();

      // Add event listeners
      scrollContainer.addEventListener("scroll", checkForOverflow);
      window.addEventListener("resize", checkForOverflow);

      // Remove event listeners on cleanup
      return () => {
        scrollContainer.removeEventListener("scroll", checkForOverflow);
        window.removeEventListener("resize", checkForOverflow);
      };
    }
  }, []);

  useEffect(() => {
    // Recheck when children change
    checkForOverflow();
  }, [children]);

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current?.querySelector(
        "[data-radix-scroll-area-viewport] .overflow-auto"
      ) as HTMLElement;
      // Cancel any ongoing animations by immediately setting the current scroll position
      container.scrollTo({
        left: container.scrollLeft,
        behavior: "auto",
      });

      const scrollDistance = container.clientWidth * 0.5;
      const targetScrollLeft = Math.max(
        0,
        container.scrollLeft - scrollDistance
      );

      // Animate scroll with smooth behavior
      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current?.querySelector(
        "[data-radix-scroll-area-viewport] .overflow-auto"
      ) as HTMLElement;
      // Cancel any ongoing animations by immediately setting the current scroll position
      container.scrollTo({
        left: container.scrollLeft,
        behavior: "auto",
      });

      const scrollDistance = container.clientWidth * 0.5;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const targetScrollLeft = Math.min(
        maxScrollLeft,
        container.scrollLeft + scrollDistance
      );

      // Animate scroll with smooth behavior
      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const continuousScrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current?.querySelector(
        "[data-radix-scroll-area-viewport] .overflow-auto"
      ) as HTMLElement;
      const scrollStep = 15; // Pixels to scroll per interval
      container.scrollBy({
        left: -scrollStep,
        behavior: "auto",
      });
      checkForOverflow();
    }
  };

  const continuousScrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current?.querySelector(
        "[data-radix-scroll-area-viewport] .overflow-auto"
      ) as HTMLElement;
      const scrollStep = 15; // Pixels to scroll per interval
      container.scrollBy({
        left: scrollStep,
        behavior: "auto",
      });
      checkForOverflow();
    }
  };

  // Handle button press and hold for continuous scrolling
  useEffectPlease(() => {
    if (isLeftButtonPressed) {
      // Initial scroll on press
      scrollLeft();

      // Start continuous scroll after a short delay
      const timeout = setTimeout(() => {
        if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = setInterval(continuousScrollLeft, 16); // ~60fps
      }, 400); // Delay before continuous scroll starts

      return () => {
        clearTimeout(timeout);
        if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
      };
    }
  }, [isLeftButtonPressed]);

  useEffectPlease(() => {
    if (isRightButtonPressed) {
      // Initial scroll on press
      scrollRight();

      // Start continuous scroll after a short delay
      const timeout = setTimeout(() => {
        if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = setInterval(continuousScrollRight, 16); // ~60fps
      }, 400); // Delay before continuous scroll starts

      return () => {
        clearTimeout(timeout);
        if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
      };
    }
  }, [isRightButtonPressed]);

  const handleLeftButtonDown = () => setIsLeftButtonPressed(true);
  const handleLeftButtonUp = () => setIsLeftButtonPressed(false);
  const handleRightButtonDown = () => setIsRightButtonPressed(true);
  const handleRightButtonUp = () => setIsRightButtonPressed(false);

  // Drag scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only start dragging if clicking on the scroll container itself, not on buttons
    const target = e.target as Element;

    // Don't start drag if clicking on a button
    if (target.closest("button")) {
      return;
    }

    setIsDragging(true);
    setDragStartX(e.clientX);

    // Try to find the actual scrollable element
    const scrollableElement = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport] .overflow-auto"
    ) as HTMLElement;
    setDragStartScrollLeft(scrollableElement?.scrollLeft || 0);
    e.preventDefault();
  };

  // Add global mouse event listeners for drag functionality
  useEffectPlease(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging || !scrollContainerRef.current) return;

      e.preventDefault();
      const deltaX = e.clientX - dragStartX;
      const newScrollLeft = dragStartScrollLeft - deltaX;

      // Try to find the actual scrollable element
      const scrollableElement = scrollContainerRef.current.querySelector(
        "[data-radix-scroll-area-viewport] .overflow-auto"
      ) as HTMLElement;
      scrollableElement.scrollLeft = newScrollLeft;
      checkForOverflow();
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStartX, dragStartScrollLeft]);

  // Apply custom scrollbar styles only on non-touch devices
  const scrollbarStyles = !isTouchDevice
    ? {
        // Hide the scrollbar while preserving scrolling functionality
        scrollbarWidth: "none" as "none",
        msOverflowStyle: "none" as "none",
        "::-webkit-scrollbar": {
          display: "none",
        },
      }
    : {};

  return (
    <div
      className={twMerge("relative group", className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
    >
      <ScrollArea
        scrollbars="horizontal"
        style={{ width: "100%", position: "relative" }}
        ref={scrollContainerRef}
      >
        <div
          className={twMerge(
            "overflow-auto flex select-none",
            isDragging ? "cursor-grabbing" : "cursor-grab"
          )}
          style={{
            ...(scrollbarStyles as any),
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onDragStart={(e) => e.preventDefault()}
          onMouseDown={handleMouseDown}
        >
          <div ref={contentRef} className={twMerge("flex", contentClassName)}>
            {children}
          </div>
        </div>
      </ScrollArea>

      {!isTouchDevice && !hideControls && showLeftArrow && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovering ? 0.8 : 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md z-10 text-black dark:bg-slate-900 dark:text-white"
          onClick={scrollLeft}
          onMouseDown={handleLeftButtonDown}
          onMouseUp={handleLeftButtonUp}
          onMouseLeave={handleLeftButtonUp}
          onTouchStart={handleLeftButtonDown}
          onTouchEnd={handleLeftButtonUp}
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </motion.button>
      )}

      {!isTouchDevice && !hideControls && showRightArrow && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovering ? 0.8 : 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md z-10 text-black dark:bg-slate-900 dark:text-white"
          onClick={scrollRight}
          onMouseDown={handleRightButtonDown}
          onMouseUp={handleRightButtonUp}
          onMouseLeave={handleRightButtonUp}
          onTouchStart={handleRightButtonDown}
          onTouchEnd={handleRightButtonUp}
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </motion.button>
      )}
    </div>
  );
};
